<script setup lang="ts">
import { ref, onMounted } from "vue";
import { Spinner } from "@/ts/components/elements";
import { useAuthStore, User } from "@/ts/stores/auth";
import { WizardPersonalInfo, WizardRenewalDecision, WizardSuccess, MembershipData, WizardPeriod, WizardSummary } from "@/ts/components/members";
import { useUrlSearchParams } from "@vueuse/core";

interface Props {
    id?: string;
}

defineProps<Props>();
const params = useUrlSearchParams("history");
const authStore = useAuthStore();
const step = ref(1);
const loading = ref(true);
const box = ref<HTMLDivElement>();
const boxHeight = ref();
const data = ref<MembershipData>({
    personal_info: {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        street: "",
        number: "",
        addition: "",
        postal_code: "",
        city: "",
        birth_date: "",
        privacy: false,
    },
    period: null,
});

function calculateBoxHeight(height: number | undefined = undefined) {
    boxHeight.value = height ? height : box.value!.offsetHeight;
}

function nextStep(property: keyof typeof data.value, value: any) {
    (data.value as Record<string, any>)[property] = value;
    step.value++;
}

function cancelationRedirect() {
    window.location.href = `/lidmaatschap-opzeggen?renewal_key=${params.renewal_key}`;
}

onMounted(() => {
    if (params && params.renewal_key) {
        calculateBoxHeight();
        authStore.readUser(Array.isArray(params.renewal_key as string[]) ? params.renewal_key[0] : (params.renewal_key as string)).then((user: User) => {
            data.value.personal_info = {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                phone: user.phone,
                street: user.street,
                number: user.number,
                addition: user.addition,
                postal_code: user.postal_code,
                city: user.city,
                birth_date: user.birth_date,
                privacy: false,
            };
            loading.value = false;
        });
    } else {
        loading.value = false;
    }
});
</script>

<template>
    <section :id="id" class="flex min-h-screen items-center transition-all duration-500 ease-in-out">
        <div class="container">
            <div class="flex justify-center">
                <div class="w-full lg:w-10/12 xl:w-8/12">
                    <div ref="box" :style="boxHeight ? `height: ${boxHeight}px` : ''" class="relative flex overflow-hidden bg-white transition-all duration-500 ease-in-out">
                        <transition-group name="fade-absolute" mode="out-in">
                            <div v-if="!loading || !(params && params.renewal_key)" class="flex-1">
                                <transition-group v-if="authStore.user" name="fade-absolute" mode="out-in">
                                    <wizard-renewal-decision v-if="step === 1" @mounted="calculateBoxHeight($event)" @renew="step = 2" @cancel="cancelationRedirect()"></wizard-renewal-decision>
                                    <wizard-period v-if="step === 2" :data="data.period" @submit="nextStep('period', $event)" @mounted="calculateBoxHeight($event)" @back="step = 1"></wizard-period>
                                    <wizard-personal-info v-if="step === 3" :show-back="true" :data="data.personal_info" @submit="nextStep('personal_info', $event)" @back="step = 2" @mounted="calculateBoxHeight($event)"></wizard-personal-info>
                                    <wizard-summary :data="data" v-if="step === 4" @mounted="calculateBoxHeight($event)" @back="step = 3"></wizard-summary>
                                </transition-group>
                                <transition-group v-else name="fade-absolute" mode="out-in">
                                    <!--
                                    <wizard-personal-info v-if="step === 1" :data="data.personal_info" @submit="nextStep('personal_info', $event)" @mounted="calculateBoxHeight($event)"></wizard-personal-info>
                                    <wizard-period v-if="step === 2" :data="data.period" @submit="nextStep('period', $event)" @mounted="calculateBoxHeight($event)" @back="step = 1"></wizard-period>
                                    <wizard-summary :data="data" v-if="step === 3" @mounted="calculateBoxHeight($event)" @back="step = 2"></wizard-summary>
                                    -->
                                    <wizard-personal-info v-if="step === 1" :data="data.personal_info" @submit="nextStep('personal_info', $event)" @mounted="calculateBoxHeight($event)"></wizard-personal-info>
                                    <wizard-success v-if="step === 2" :data="data" @mounted="calculateBoxHeight($event)"></wizard-success>
                                </transition-group>
                            </div>
                            <span v-else class="flex flex-1 items-center justify-center p-20">
                                <spinner></spinner>
                            </span>
                        </transition-group>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
